"use client";

import { useEffect } from "react";
import Script from "next/script";
import { consumeGtmSignup, pushGtmSignup } from "@/lib/gtmSignup";

/**
 * Google Tag Manager container.
 *
 * Deliberately NOT mounted in the root layout: tracking is only wanted on the
 * public site and the student dashboard, never on the teacher or admin
 * dashboards. So this is rendered from those two layouts instead
 * (`app/[locale]/(web)/layout.tsx` and
 * `app/[locale]/(dashboard)/student/layout.tsx`).
 *
 * `afterInteractive` is the recommended strategy for tag managers - the
 * container loads right after hydration, so it never blocks first paint or
 * delays any first-party code. Rendering the component twice (e.g. a student
 * navigating from the site into the dashboard) is harmless: `next/script`
 * de-duplicates by `id`, so the container is injected once per page load.
 *
 * The <noscript> iframe fallback is kept for parity with the plain-HTML
 * snippet; it only ever renders when JS is disabled.
 */

const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID ?? "GTM-TTZKGCT";

export default function GoogleTagManager() {
  // Replays the `signUp` conversion stashed by the verify page onto dataLayer,
  // now that we are on a page that actually has the container. Kept above the
  // GTM_ID guard so the hook order stays unconditional; `consumeGtmSignup()`
  // clears the stash as it reads, so this pushes at most once even under
  // development's double-invoked effects. Nothing happens on the vast majority
  // of page loads, where there is no stash to consume.
  useEffect(() => {
    const student = consumeGtmSignup();
    if (student) {
      pushGtmSignup(student);
    }
  }, []);

  if (!GTM_ID) {
    return null;
  }

  return (
    <>
      {/*
        The dataLayer bootstrap the standard GTM snippet ships with. Without the
        `gtm.js` event push, container triggers that fire on page view (the
        "All Pages" trigger every tag defaults to) never fire, so the container
        would load and then do nothing. It must run before gtm.js executes -
        `next/script` runs same-strategy scripts in render order, and gtm.js
        additionally has to come back over the network first.
      */}
      <Script id="gtm-datalayer" strategy="afterInteractive">
        {`window.dataLayer=window.dataLayer||[];window.dataLayer.push({'gtm.start':new Date().getTime(),event:'gtm.js'});`}
      </Script>
      <Script
        id="gtm-script"
        src={`https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`}
        strategy="afterInteractive"
      />
      <noscript>
        <iframe
          src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`}
          height="0"
          width="0"
          style={{ display: "none", visibility: "hidden" }}
          title="Google Tag Manager"
        />
      </noscript>
    </>
  );
}
